import React, { useState, useEffect } from 'react';
import AboutUs from './ui/AboutUs';
import ContactUs from './ui/ContactUs';
import { useRankingLogic } from '../hooks/useRankingLogic';
import SearchAndFilter from './ui/SearchAndFilter';
import PersonList from './ui/PersonList';
import SignupPromptModal from './ui/SignupPromptModal';
import OutOfKarmaModal from './ui/OutOfKarmaModal';
import { useAuth } from '../contexts/AuthContext';
import './Rankings.css';

function Ranking({ persons = [] }) {
  const { currentUser } = useAuth();
  const {
    filteredPersons,
    votes,
    handleVote,
    searchTerm,
    setSearchTerm,
    selectedCategory,
    setSelectedCategory,
    voteError,
    clearVoteError
  } = useRankingLogic(persons);

  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showOutOfKarmaModal, setShowOutOfKarmaModal] = useState(false);

  useEffect(() => {
    if (voteError && voteError.type === 'INSUFFICIENT_POINTS') {
      if (!currentUser) {
        setShowSignupModal(true);
      } else {
        setShowOutOfKarmaModal(true);
      }
      clearVoteError();
    }
  }, [voteError, currentUser, clearVoteError]);

  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const meaningfulScrollRange = 4000;
      const progress = Math.min(scrollPosition / meaningfulScrollRange, 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getBackgroundColor = (p) => {
    const lerp = (start, end, t) => Math.round(start + (end - start) * t);
    let r, g, b;

    if (p < 0.5) {
      // 0.0 -> Deep Violet: #581c87 (88, 28, 135) - Premium/Mystic
      // 0.5 -> Royal Indigo: #3730a3 (55, 48, 163) - Rich
      const t = p * 2;
      r = lerp(88, 55, t);
      g = lerp(28, 48, t);
      b = lerp(135, 163, t);
    } else {
      // 0.5 -> Royal Indigo: #3730a3 (55, 48, 163)
      // 1.0 -> Slate Base: #0f172a (15, 23, 42)
      const t = (p - 0.5) * 2;
      r = lerp(55, 15, t);
      g = lerp(48, 23, t);
      b = lerp(163, 42, t);
    }
    return `rgb(${r}, ${g}, ${b})`;
  };

  const backgroundColor = getBackgroundColor(scrollProgress);

  const isInitialLoading = persons.length > 0 && filteredPersons.length === 0 && searchTerm === '' && selectedCategory === 'All';

  return (
    <div className="Rankings-container">
      <div
        className="fixed-background"
        style={{
          background: `linear-gradient(180deg, ${backgroundColor} 0%, #0f172a 100%)`,
        }}
      />

      <SearchAndFilter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
      />

      {isInitialLoading ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading rankings...</p>
        </div>
      ) : (
        <PersonList
          persons={filteredPersons}
          votes={votes}
          handleVote={handleVote}
          searchTerm={searchTerm}
          selectedCategory={selectedCategory}
        />
      )}
      <AboutUs />
      <ContactUs />

      <SignupPromptModal
        isOpen={showSignupModal}
        onClose={() => setShowSignupModal(false)}
      />

      <OutOfKarmaModal
        isOpen={showOutOfKarmaModal}
        onClose={() => setShowOutOfKarmaModal(false)}
      />
    </div>
  );
}

export default Ranking;

